import { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Card,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Iconify from '../../components/iconify';
import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import Loading from '../../layouts/loading';
import { ReimbursementRequestForm, ReimbursementTable, ReimbursementFilters } from './components';
import usePersistedState from '../../hooks/usePersistedState';
import {
  createReimbursementRequest,
  getMyReimbursements,
  getAllReimbursements,
  cancelReimbursement,
  updateReimbursementStatus,
  updateReimbursementRequest,
} from '../../services/reimbursementService';
import { isAdmin, isManager, isHR, checkComponentPermission } from '../../utils/share';

const queryClient = new QueryClient();

function ReimbursementsContent() {
  const [openFormModal, setOpenFormModal] = useState(false);
  const [editingReimbursement, setEditingReimbursement] = useState(null);
  const isAdminUser = isAdmin();
  const isManagerUser = isManager();
  const isHRUser = isHR();
  // Persisted so returning from a request's details page lands back on the same tab, filters and sort
  const [activeTab, setActiveTab] = usePersistedState(
    'reimbursements.activeTab',
    (isManagerUser || isHRUser) && !isAdminUser ? 1 : 0
  );
  const [filters, setFilters] = usePersistedState('reimbursements.filters', {});
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25 });
  // { sortBy, sortOrder } — empty means the backend default (pending first, newest first)
  const [sort, setSort] = usePersistedState('reimbursements.sort', {});
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingReimbursementId, setCancelingReimbursementId] = useState(null);
  const user = useSelector((state) => state?.login?.user || null);
  const userId = user?.id;
  const isAdminOrManagerOrHR = isAdminUser || isManagerUser || isHRUser;

  const queryClientInstance = useQueryClient();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === undefined || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      setPagination((p) => ({ ...p, page: 1 }));
      return newFilters;
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination((p) => ({ ...p, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination({ page: 1, pageSize: parseInt(event.target.value, 10) });
  };

  const handleSort = (columnId) => {
    setSort((prev) => ({
      sortBy: columnId,
      sortOrder: prev.sortBy === columnId && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const { data: myReimbursementsData = { rows: [], count: 0 }, isLoading: isLoadingMine } = useQuery({
    queryKey: ['myReimbursements', filters, pagination, sort],
    queryFn: async () => {
      try {
        const response = await getMyReimbursements({ ...filters, ...sort }, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch {
        toast.error('Error fetching your reimbursement requests');
        return { rows: [], count: 0 };
      }
    },
  });

  const { data: allReimbursementsData = { rows: [], count: 0 }, isLoading: isLoadingAll } = useQuery({
    queryKey: ['allReimbursements', filters, pagination, sort],
    queryFn: async () => {
      try {
        const response = await getAllReimbursements({ ...filters, ...sort }, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch {
        toast.error('Error fetching all reimbursement requests');
        return { rows: [], count: 0 };
      }
    },
    enabled: isAdminOrManagerOrHR,
  });

  const myReimbursements = myReimbursementsData.rows || [];
  const myCount = myReimbursementsData.count || 0;
  const allReimbursements = allReimbursementsData.rows || [];
  const allCount = allReimbursementsData.count || 0;

  const createMutation = useMutation({
    mutationFn: createReimbursementRequest,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Reimbursement request submitted successfully');
      setOpenFormModal(false);
      setEditingReimbursement(null);
      queryClientInstance.invalidateQueries(['myReimbursements']);
      if (isAdminOrManagerOrHR) queryClientInstance.invalidateQueries(['allReimbursements']);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error submitting reimbursement request');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateReimbursementRequest(id, data),
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Reimbursement request updated successfully');
      setOpenFormModal(false);
      setEditingReimbursement(null);
      queryClientInstance.invalidateQueries(['myReimbursements']);
      if (isAdminOrManagerOrHR) queryClientInstance.invalidateQueries(['allReimbursements']);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error updating reimbursement request');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: cancelReimbursement,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Reimbursement request cancelled successfully');
      queryClientInstance.invalidateQueries(['myReimbursements']);
      if (isAdminOrManagerOrHR) queryClientInstance.invalidateQueries(['allReimbursements']);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error cancelling reimbursement request');
    },
  });

  const handleCreateClick = () => {
    setEditingReimbursement(null);
    setOpenFormModal(true);
  };

  const handleEditReimbursement = useCallback(
    (reimbursementId) => {
      const item =
        activeTab === 0
          ? myReimbursements.find((r) => r.id === reimbursementId)
          : allReimbursements.find((r) => r.id === reimbursementId);
      if (item) {
        setEditingReimbursement(item);
        setOpenFormModal(true);
      }
    },
    [activeTab, myReimbursements, allReimbursements]
  );

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
    setEditingReimbursement(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (values) => {
      if (editingReimbursement) {
        await updateMutation.mutateAsync({ id: editingReimbursement.id, data: values });
      } else {
        await createMutation.mutateAsync(values);
      }
      return true;
    },
    [createMutation, updateMutation, editingReimbursement]
  );

  const handleCancelReimbursement = useCallback((reimbursementId) => {
    setCancelingReimbursementId(reimbursementId);
    setCancelDialogOpen(true);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (cancelingReimbursementId) {
      cancelMutation.mutate(cancelingReimbursementId);
      setCancelDialogOpen(false);
      setCancelingReimbursementId(null);
    }
  }, [cancelingReimbursementId, cancelMutation]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const effectiveTab = activeTab;
  const isLoading = effectiveTab === 0 ? isLoadingMine : isLoadingAll;
  const currentReimbursements = effectiveTab === 0 ? myReimbursements : allReimbursements;
  const currentCount = effectiveTab === 0 ? myCount : allCount;

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Reimbursements', path: '' },
  ];

  return (
    <>
      <Helmet>
        <title>Reimbursements</title>
      </Helmet>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            {effectiveTab === 0 && checkComponentPermission('CREATE_REIMBURSEMENT') && (
              <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleCreateClick}>
                Request Reimbursement
              </Button>
            )}
          </Stack>
        </Stack>

        {isAdminOrManagerOrHR ? (
          <Card sx={{ mb: 3 }}>
            <Tabs value={effectiveTab} onChange={handleTabChange}>
              <Tab label="My Requests" />
              <Tab label="All Requests" />
            </Tabs>
          </Card>
        ) : (
          <Card sx={{ mb: 3 }}>
            <Tabs value={0} sx={{ pointerEvents: 'none' }}>
              <Tab label="My Requests" />
            </Tabs>
          </Card>
        )}

        <ReimbursementFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          showEmployeeFilter={effectiveTab === 1 && isAdminOrManagerOrHR}
        />

        <Loading open={isLoading} />
        <Card>
          <Box sx={{ p: 3 }}>
            <ReimbursementTable
              reimbursements={currentReimbursements}
              onCancelReimbursement={handleCancelReimbursement}
              onEditReimbursement={handleEditReimbursement}
              showEmployee={effectiveTab === 1}
              currentUserId={userId}
              count={currentCount}
              page={pagination.page - 1}
              rowsPerPage={pagination.pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              sort={sort}
              onSort={handleSort}
            />
          </Box>
        </Card>

        {openFormModal && (
          <ReimbursementRequestForm
            open
            onClose={handleFormModalClose}
            onSubmit={handleFormSubmit}
            reimbursementData={editingReimbursement}
            isEdit={!!editingReimbursement}
          />
        )}

        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
          <DialogTitle>Cancel Reimbursement Request</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to cancel this reimbursement request? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={handleConfirmCancel}
              loading={cancelMutation.isLoading}
            >
              Yes, Cancel
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}

export default function ReimbursementsPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <ReimbursementsContent />
    </QueryClientProvider>
  );
}
